// E1 Phase 8 — Workspace billing API client.
//
// Covers the read/list + per-workspace credit report/budget REST endpoints and the
// active-context WS switch. CREDITS ONLY — every amount is an integer credit
// count; the wire never carries currency (the firm applies its own rate card).
import { create as createProto } from '@bufbuild/protobuf';

import { AuthError, apiFetch } from '@/lib/http';
import { createEnvelope } from '@/lib/protobuf/codec';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import {
  AddSnapshotTagRequestSchema,
  CreateSnapshotRequestSchema,
  ExportWorkspaceRequestSchema,
  type ExportWorkspaceResponse,
  GetSnapshotRequestSchema,
  ListSnapshotsRequestSchema,
  type ListSnapshotsResponse,
  MountWorkspaceRequestSchema,
  type MountWorkspaceResponse,
  RekeyWorkspaceRequestSchema,
  type RekeyWorkspaceResponse,
  SetActiveWorkspaceRequestSchema,
  SetSnapshotLegalHoldRequestSchema,
  type Snapshot,
  type SnapshotContentResponse,
  type SnapshotResponse,
  type SnapshotTag,
  type TransferContentCompletedEvent,
  type TransferContentResponse,
  TransferContentRequestSchema,
  UnmountWorkspaceRequestSchema,
  type UnmountWorkspaceResponse,
} from '@/lib/protobuf/gen/workspace/workspace_pb';
import { sendRequest, wsManager } from '@/lib/websocket';

import type { DevicePubKey, WrappedKeyRow, OrgEscrowDeviceMaterial } from './workspaceCrypto';

/** A Workspace = an encrypted island. Mirrors the Go wire shape
 *  (snake_case) of internal/workspace.Workspace. */
export interface Workspace {
  id: string;
  org_id: string;
  name: string;
  owner_user_id: string;
  escrow_enabled: boolean;
  status: string;
  /** Optional per-workspace credit cap; absent ⇒ uncapped. */
  budget_credits?: number;
  /** 'standard' or 'always_on' (AON-03). Absent from an older backend ⇒ standard. */
  kind?: string;
  /** MEM-09m: owner + grantees. Absent ⇒ not known (never render 0). */
  member_count?: number;
  created_at: string;
  updated_at: string;
}

/** The per-workspace credit report from GET /:id/usage. Credits only. */
export interface WorkspaceUsage {
  workspace_id: string;
  spent_credits: number;
  /** Absent ⇒ uncapped workspace. */
  budget_credits?: number;
  /** Absent ⇒ uncapped; clamped to ≥0 when overspent. */
  remaining_credits?: number;
}

/** Lists the workspaces the caller owns/can see (GET /api/v2/workspaces). */
export async function listWorkspaces(): Promise<Workspace[]> {
  const ws = await apiFetch<Workspace[]>('/api/v2/workspaces', { method: 'GET' });
  return ws ?? [];
}

/** An org the caller belongs to — a workspace is created inside one of these.
 *  Mirrors auth.OrgMembership (note: its OrgID carries the json tag `id`). */
export interface OrgOption {
  id: string;
  name: string;
  role: string;
}

/** Lists the caller's orgs (GET /api/v2/organizations → { organizations }).
 *  Every user has at least a personal org created at registration, so this is
 *  non-empty in practice. */
export async function listMyOrgs(): Promise<OrgOption[]> {
  const res = await apiFetch<{ organizations: OrgOption[] }>('/api/v2/organizations', {
    method: 'GET',
  });
  return res?.organizations ?? [];
}

/** Creates a Workspace in an org (POST /api/v2/workspaces).
 *  The caller must be a member of `orgId` (enforced server-side). */
export async function createWorkspace(name: string, orgId: string): Promise<Workspace> {
  return apiFetch<Workspace>('/api/v2/workspaces', {
    method: 'POST',
    body: JSON.stringify({ name: name.trim(), org_id: orgId }),
  });
}

/** Fetches one workspace's credit report (owner/org-admin only). */
export async function getWorkspaceUsage(workspaceId: string): Promise<WorkspaceUsage> {
  return apiFetch<WorkspaceUsage>(`/api/v2/workspaces/${encodeURIComponent(workspaceId)}/usage`, {
    method: 'GET',
  });
}

/** Sets (number) or clears (null) a workspace's credit cap; returns the new report. */
export async function setWorkspaceBudget(
  workspaceId: string,
  budgetCredits: number | null,
): Promise<WorkspaceUsage> {
  return apiFetch<WorkspaceUsage>(`/api/v2/workspaces/${encodeURIComponent(workspaceId)}/budget`, {
    method: 'PUT',
    body: JSON.stringify({ budget_credits: budgetCredits }),
  });
}

/**
 * Switches the session's active billing-context workspace over the WS (envelope 621).
 * An empty workspaceId clears it. The workspace must be MOUNTED in this session for
 * the backend to honour it (it answers with SetActiveWorkspaceResponse status
 * "active"/"cleared", or an error envelope 409 when unmounted). Fire-and-forget;
 * the store reconciles on the response.
 */
export function sendSetActiveWorkspace(workspaceId: string): void {
  const req = createProto(SetActiveWorkspaceRequestSchema, { workspaceId });
  wsManager.sendEnvelope(createEnvelope({ case: 'setActiveWorkspaceRequest', value: req }));
}

/** The whole-item kinds a transfer can carry (E1 Phase 7; `task` since AON-06,
 *  into and out of the caller's always-on space only). */
export type TransferItemKind = 'document' | 'note' | 'research' | 'task';

/** Typed refusal codes a transfer response (`status:"refused"`) or a failed
 *  completion event carries (AON-06). The dialog renders each from an i18n key,
 *  never the server string. */
export type TransferErrorCode =
  | 'copy_into_always_on'
  | 'not_movable_yet'
  | 'note_exists'
  | 'always_on_refused'
  // MEM-09n: the chosen context node is gone, archived or not in the Workspace.
  | 'target_node_invalid'
  // MEM-09n: a personal item already in this standard Workspace (Preview refuses).
  | 'already_in_workspace';

const TRANSFER_ERROR_CODES: ReadonlySet<string> = new Set<TransferErrorCode>([
  'copy_into_always_on',
  'not_movable_yet',
  'note_exists',
  'always_on_refused',
  'target_node_invalid',
  'already_in_workspace',
]);

/** Narrows a wire `error_code` to a known code; anything else (including "") is null. */
export function asTransferErrorCode(code: string | undefined | null): TransferErrorCode | null {
  return code && TRANSFER_ERROR_CODES.has(code) ? (code as TransferErrorCode) : null;
}

/** Copy keeps the source; move deletes it after a successful write. */
export type TransferMode = 'copy' | 'move';

export interface TransferContentArgs {
  /** Item being moved/copied out of the user's personal area. */
  item: { kind: TransferItemKind; id: string };
  /** Destination workspace (a mounted, writable workspace, or the caller's
   *  always-on space). Ignored for `direction: 'out'`. */
  targetWorkspaceId: string;
  mode: TransferMode;
  /** 'in' (default): personal → workspace. 'out' (AON-06): the always-on space
   *  → personal, with `sourceWorkspaceId` naming the space. */
  direction?: 'in' | 'out';
  /** The workspace the item leaves, for `direction: 'out'`. */
  sourceWorkspaceId?: string;
  /**
   * Explicit consent to expose the item to the workspace's members. The first
   * send is always `false`; the server replies `consent_required` with the
   * member count, then the client re-sends the SAME request with `true`.
   */
  confirm: boolean;
  /**
   * MEM-09n: the context node of the target Workspace's graph the item lands on.
   * `""` or absent ⇒ Unsorted. Only a standard (not always-on) Workspace target
   * carries one.
   */
  targetNodeId?: string;
}

/**
 * Sends a TransferContentRequest (envelope 623) to move/copy one item from the
 * caller's personal area into a shared Workspace island and resolves with the
 * SYNCHRONOUS ack (`transferContentResponse`, envelope 624), correlated by
 * request id via `sendRequest`:
 *   - `status:"consent_required"` + `exposedMemberCount` ⇒ re-call with
 *     `confirm:true` after the user confirms exposure.
 *   - `status:"accepted"` + `transferId` ⇒ the transfer runs on a detached
 *     server goroutine; a `transferContentCompletedEvent` (625) follows on the
 *     same connection, keyed by `transferId` (see `subscribeTransferCompleted`).
 *
 * Source is the personal area (`kind:'personal'`, empty workspaceId), except
 * for `direction:'out'` (AON-06), where the source is the caller's always-on
 * space and the target is the personal area. ws→ws has no FE entry point.
 *
 * `status:"refused"` + `errorCode` (AON-06) is a typed refusal — see
 * {@link TransferErrorCode}.
 *
 * Rejects (typed `RequestError`) on a server error envelope or the request
 * timeout. This feature surfaces NO cost — the request carries no
 * credit/currency field.
 */
export function sendTransferContent(args: TransferContentArgs): Promise<TransferContentResponse> {
  // AON-06: `out` moves an item from the caller's always-on space back to the
  // personal area — the same request with source and target swapped.
  const out = args.direction === 'out';
  const req = createProto(TransferContentRequestSchema, {
    source: out
      ? { kind: 'workspace', workspaceId: args.sourceWorkspaceId ?? '' }
      : { kind: 'personal', workspaceId: '' },
    target: out ? { kind: 'personal', workspaceId: '' } : { kind: 'workspace', workspaceId: args.targetWorkspaceId },
    item: { kind: args.item.kind, id: args.item.id },
    mode: args.mode,
    confirm: args.confirm,
    targetNodeId: args.targetNodeId ?? '',
  });
  return sendRequest({ case: 'transferContentRequest', value: req }, 'transferContentResponse');
}

/**
 * Subscribes to the async completion event for ONE accepted transfer, matched by
 * `transferId` (per-request correlation — NOT a single module-level slot, so two
 * in-flight transfers never clobber each other; CLAUDE.md FE race rule). `cb`
 * fires at most once; the subscription auto-unsubscribes after the first match.
 * Returns an unsubscribe for the caller to invoke on unmount/timeout.
 */
export function subscribeTransferCompleted(
  transferId: string,
  cb: (event: TransferContentCompletedEvent) => void,
): () => void {
  const off = wsManager.onMessage('transferContentCompletedEvent', (env: MessageEnvelope) => {
    if (env.payload.case !== 'transferContentCompletedEvent') return;
    const value = env.payload.value as TransferContentCompletedEvent;
    if (value.transferId !== transferId) return;
    off();
    cb(value);
  });
  return off;
}

// ===========================================================================
// E1 Phase 13 — Workspace lifecycle (create-E2E, devices, key-material, grants,
// escrow, mount/unmount/re-key, snapshots). Crypto orchestration lives in
// workspaceCrypto.ts; this module is the wire (REST + WS) only.
// ===========================================================================

/** A member's permission on a workspace. */
export type AccessLevel = 'read' | 'read_write';

/** A workspace membership grant (GET /:id/grants). Mirrors workspace.Grant. */
export interface Grant {
  workspace_id: string;
  grantee_user_id: string;
  access_level: AccessLevel;
  granted_by: string;
  rekey_pending: boolean;
  granted_at: string;
}

/** Lists THIS user's enrolled devices with public keys (GET /api/v1/crypto/devices).
 *  Needed to wrap a client-generated WS-DEK to all of the owner's devices at create. */
export async function listMyDevices(opts?: { signal?: AbortSignal }): Promise<DevicePubKey[]> {
  const res = await apiFetch<{ devices: DevicePubKey[] }>('/api/v1/crypto/devices', {
    method: 'GET',
    signal: opts?.signal ?? null,
  });
  return res?.devices ?? [];
}

/** Creates a Workspace with a CLIENT-generated WS-DEK pre-wrapped to the owner's
 *  devices (E1 Phase 13 — the server never sees the plaintext DEK). */
export async function createWorkspaceE2E(
  name: string,
  orgId: string,
  wrappedDEKMaterial: WrappedKeyRow[],
): Promise<Workspace> {
  return apiFetch<Workspace>('/api/v2/workspaces', {
    method: 'POST',
    body: JSON.stringify({
      name: name.trim(),
      org_id: orgId,
      wrapped_ws_dek_material: wrappedDEKMaterial,
    }),
  });
}

/** The caller's own per-device wrapped WS-DEK rows (GET /:id/key-material) — the
 *  read that unblocks mounting: unwrap with the device key, then send the DEK. */
export async function getMyKeyMaterial(workspaceId: string): Promise<WrappedKeyRow[]> {
  const rows = await apiFetch<WrappedKeyRow[]>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/key-material`,
    { method: 'GET' },
  );
  return rows ?? [];
}

/** Lists a workspace's grants (owner/org-admin only). */
export async function listGrants(workspaceId: string): Promise<Grant[]> {
  const grants = await apiFetch<Grant[]>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/grants`,
    { method: 'GET' },
  );
  return grants ?? [];
}

/** A prospective grantee resolved by email (GET /:id/grant-candidates).
 *  Mirrors workspace.GrantCandidate (snake_case wire). */
export interface GrantCandidate {
  user_id: string;
  display_name: string;
  email: string;
}

/**
 * Resolves a colleague's email → user for the grant dialog
 * (GET /:id/grant-candidates?email=). Exact-match, case-insensitive. Gated by
 * the SAME authz as POST grants (workspace owner / org owner-admin), so it is
 * not an open user-directory. Throws `AuthError` with status 404 when no
 * active account matches — the dialog renders that inline.
 */
export async function lookupGrantCandidate(
  workspaceId: string,
  email: string,
): Promise<GrantCandidate> {
  return apiFetch<GrantCandidate>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/grant-candidates?email=${encodeURIComponent(email.trim().toLowerCase())}`,
    { method: 'GET' },
  );
}

/** Lists a prospective grantee's device public keys (so the actor can wrap to them). */
export async function getGranteeDevices(
  workspaceId: string,
  granteeUserId: string,
): Promise<DevicePubKey[]> {
  const devs = await apiFetch<DevicePubKey[]>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/grantees/${encodeURIComponent(granteeUserId)}/devices`,
    { method: 'GET' },
  );
  return devs ?? [];
}

/** Grants a member access, supplying the WS-DEK pre-wrapped to their devices. */
export async function grantMember(
  workspaceId: string,
  granteeUserId: string,
  accessLevel: AccessLevel,
  wrappedKeys: WrappedKeyRow[],
): Promise<void> {
  await apiFetch<void>(`/api/v2/workspaces/${encodeURIComponent(workspaceId)}/grants`, {
    method: 'POST',
    body: JSON.stringify({
      grantee_user_id: granteeUserId,
      access_level: accessLevel,
      wrapped_keys: wrappedKeys,
    }),
  });
}

/** Revokes a member's access (DELETE /:id/grants/:uid). Flags rekey_pending
 *  server-side — the caller should re-key next. */
export async function revokeMember(workspaceId: string, granteeUserId: string): Promise<void> {
  await apiFetch<void>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/grants/${encodeURIComponent(granteeUserId)}`,
    { method: 'DELETE' },
  );
}

// --- Escrow (P6) ---

/** The org escrow public key, or null if the org has not initialized escrow. */
export async function getOrgEscrowPublicKey(orgId: string): Promise<string | null> {
  try {
    const res = await apiFetch<{ escrow_public_key: string }>(
      `/api/v2/organizations/${encodeURIComponent(orgId)}/escrow/public_key`,
      { method: 'GET' },
    );
    return res?.escrow_public_key ?? null;
  } catch {
    return null; // 404 ⇒ not initialized
  }
}

/** One-time org escrow bootstrap: the escrow pubkey + the escrow privkey wrapped
 *  to each org-owner device (POST /organizations/:id/escrow/init). */
export async function initOrgEscrow(
  orgId: string,
  publicKey: string,
  ownerMaterial: OrgEscrowDeviceMaterial[],
): Promise<void> {
  await apiFetch<void>(`/api/v2/organizations/${encodeURIComponent(orgId)}/escrow/init`, {
    method: 'POST',
    body: JSON.stringify({ escrow_public_key: publicKey, owner_device_material: ownerMaterial }),
  });
}

/** Enables escrow on a workspace (the WS-DEK wrapped to the org escrow pubkey). */
export async function enableEscrow(workspaceId: string, escrowWrappedWSDEK: string): Promise<void> {
  await apiFetch<void>(`/api/v2/workspaces/${encodeURIComponent(workspaceId)}/escrow`, {
    method: 'POST',
    body: JSON.stringify({ escrow_wrapped_ws_dek: escrowWrappedWSDEK }),
  });
}

/** Recovery payload (org owner): the escrow-wrapped WS-DEK + the escrow privkey
 *  wrapped to the recovery device (GET /:id/escrow/recovery?device_id=). */
export interface EscrowRecovery {
  escrow_wrapped_ws_dek: string;
  wrapped_escrow_private_key: string;
}
export async function getEscrowRecovery(
  workspaceId: string,
  deviceId: string,
): Promise<EscrowRecovery> {
  return apiFetch<EscrowRecovery>(
    `/api/v2/workspaces/${encodeURIComponent(workspaceId)}/escrow/recovery?device_id=${encodeURIComponent(deviceId)}`,
    { method: 'GET' },
  );
}

// --- Mount / unmount / re-key (WS, request/response correlated) ---

/** Mounts a workspace: sends the plaintext WS-DEK over the (TLS) WS so the owning
 *  backend can open the encrypted island. Resolves with the mount handshake
 *  (access level). Uses sendRequest ⇒ built-in 30s timeout → reject. */
export function sendMountWorkspace(
  workspaceId: string,
  wsDek: Uint8Array,
  opts?: { keepActiveWorkspace?: boolean },
): Promise<MountWorkspaceResponse> {
  // keepActiveWorkspace (AON-03 D59): mount without making it the session's
  // active billing workspace — the silent always-on space auto-mount uses it.
  const req = createProto(MountWorkspaceRequestSchema, {
    workspaceId,
    wsDek,
    keepActiveWorkspace: opts?.keepActiveWorkspace ?? false,
  });
  return sendRequest({ case: 'mountWorkspaceRequest', value: req }, 'mountWorkspaceResponse');
}

/** One org in an always-on policy decision (AON-09). `name` may be empty. */
export interface AlwaysOnOrgRef {
  id: string;
  name: string;
}

/** The owner's always-on space as GET /api/v2/always-on-space reports it
 *  (snake_case wire, mirrors `alwayson.Status` in
 *  internal/workspace/alwayson/types.go field for field). */
export interface AlwaysOnSpaceStatus {
  workspace_id: string;
  name?: string;
  /** Only the exact `'on'` is on. The client treats any other string the
   *  server might send as not-on (fail closed, `selectBannerState`). */
  state?: 'on' | 'off';
  /** True while the key from the last switch-off is still being destroyed;
   *  switch-on is refused (409 `key_destruction_pending`) until it clears. */
  key_destruction_pending?: boolean;
  /** True while the space is on but its purposes are not confirmed registered at
   *  the key service: offline work may be refused. Retry is a repeat switch-on.
   *  Absent (an older backend) reads as false. */
  purposes_pending?: boolean;
  /** Present while the space is suspended — the server sends it with the
   *  feature flag off (`reason: "kill_switch"`). A suspended space is still
   *  mounted (switch-off, export) but takes no moves. With `reason:
   *  "org_policy"`, `orgs` names the orgs that forbid it (AON-09). The wire
   *  sends `null` when not suspended. */
  suspended?: {
    reason: 'kill_switch' | 'org_policy' | 'plan';
    org_ids?: string[];
    orgs?: AlwaysOnOrgRef[];
  } | null;
  /** Absent = not known. Never rendered as 0. */
  size_bytes?: number;
  /** The consent text version accepted at the last switch-on; absent for a
   *  space that was never switched on. */
  consent_version?: string;
  consent_accepted_at?: string;
  created_at?: string;
}

/** One write recorded by an access-log open (`alwayson.WriteItem`). */
export interface AlwaysOnLogWriteItem {
  kind: string;
  ref?: string;
}

/** One access-log row as GET /api/v2/always-on-space/access-log returns it
 *  (`alwayson.LogEntry` = `Entry` + `verifiable`; every json tag copied from
 *  internal/workspace/alwayson/accesslog_entry.go and accesslog_read.go). */
export interface AlwaysOnLogEntry {
  id: string;
  /** A newer backend may send a kind this build does not know; LogRow shows it as "Other entry". */
  kind: 'open' | 'bridge_crossing' | 'unreadable' | 'compelled' | (string & {});
  at: string;
  // open
  /** workflow | connector | inbound | deposit */
  purpose_kind?: string;
  object_id?: string;
  run_id?: string;
  /** The workflow or connector name, set by the purpose handler. */
  label?: string;
  unwrap_id?: string;
  epoch?: number;
  /** Absent: opened, not closed (a crash, or still running). */
  closed_at?: string;
  duration_ms?: number;
  /** Absent until the open finished: "not known" is not 0. */
  objects_written?: number;
  items?: AlwaysOnLogWriteItem[];
  items_dropped?: number;
  /** The purpose handler returned an error. */
  failed?: boolean;
  /** pending | posted | gone */
  receipt?: string;
  // bridge_crossing
  direction?: 'in' | 'out';
  object_ref?: string;
  automation?: string;
  landed_at?: string;
  // compelled (AON-14; json tags from accesslog_entry.go, each omitted when empty)
  /** The day the order was carried out, `YYYY-MM-DD`. */
  occurred_on?: string;
  jurisdiction?: string;
  reference?: string;
  /** True only for an open of the current keyd epoch while the space is on. */
  verifiable: boolean;
}

/** One opening keyd recorded that the log has no entry for (`alwayson.UnloggedOpen`). */
export interface AlwaysOnUnloggedOpen {
  unwrap_id: string;
  at: string;
  purpose_kind: string;
  object_id: string;
}

/** GET /api/v2/always-on-space/access-log's body (`alwayson.AccessLogPage`). */
export interface AlwaysOnAccessLogPage {
  entries: AlwaysOnLogEntry[];
  /** The cursor for the next (older) page; "" on the last page. */
  next: string;
  /** Rows of THIS page that did not decode; each is also in `entries`. */
  unreadable: number;
  integrity: {
    state: 'ok' | 'mismatch' | 'unknown' | 'not_applicable';
    unlogged_opens: number;
    /** Newest first, at most 20; never null on the wire. */
    unlogged: AlwaysOnUnloggedOpen[];
    epoch?: number;
    checked_at: string;
  };
}

// The five always-on mutations and reads below (and listMyDevices above) take
// an optional `signal` so a caller that times them out (alwayson
// `boundedCall`) aborts the request, not only the wait.

/** POST /api/v2/always-on-space (AON-08): creates the caller's always-on space
 *  with a CLIENT-generated WS-DEK pre-wrapped to the owner's devices. Returns
 *  the workspace row (201). The space is created switched off. */
export function createAlwaysOnSpace(
  name: string,
  wrappedDEKMaterial: WrappedKeyRow[],
  opts?: { signal?: AbortSignal },
): Promise<Workspace> {
  return apiFetch<Workspace>('/api/v2/always-on-space', {
    method: 'POST',
    body: JSON.stringify({ name, wrapped_ws_dek_material: wrappedDEKMaterial }),
    signal: opts?.signal ?? null,
  });
}

/** POST /api/v2/always-on-space/on (AON-08): switches the space on under the
 *  consent text version the user read. Returns the status after the switch.
 *  `requireOn` is the purposes-pending Retry: the server repeats the switch-on
 *  only for a space that is on, and answers 409 `space_off` for an off one, so
 *  a stale status cannot switch a space back on without the consent dialog. */
export function switchOnAlwaysOnSpace(
  consentVersion: string,
  opts?: { signal?: AbortSignal; requireOn?: boolean },
): Promise<AlwaysOnSpaceStatus> {
  return apiFetch<AlwaysOnSpaceStatus>('/api/v2/always-on-space/on', {
    method: 'POST',
    body: JSON.stringify(
      opts?.requireOn ? { consent_version: consentVersion, require_on: true } : { consent_version: consentVersion },
    ),
    signal: opts?.signal ?? null,
  });
}

/** One row of GET /api/v2/always-on-space/workflows (AON-05; lowerCamelCase,
 *  internal/workflow/spacerun/handler.go `bindingResponse`). */
export interface WorkflowBinding {
  workflowId: string;
  createdAt: string;
}

// The three workflow-binding calls (AON-07). Like the calls above they are bare
// apiFetch with no timeout of their own: a caller runs them through alwayson
// `boundedCall`, which hands in the signal it aborts on timeout.

/** GET /api/v2/always-on-space/workflows: the workflows that run in the
 *  caller's always-on space. */
export function listWorkflowBindings(signal?: AbortSignal): Promise<{ workflows: WorkflowBinding[] }> {
  return apiFetch<{ workflows: WorkflowBinding[] }>('/api/v2/always-on-space/workflows', {
    method: 'GET',
    signal: signal ?? null,
  });
}

/** PUT /api/v2/always-on-space/workflows/:id (AON-07): moves the workflow into
 *  the space. The server re-keys its store into the space before it answers.
 *  Refusals carry a `code` (see alwayson/errors.ts `alwaysOnErrorKey`). */
export function bindWorkflow(workflowId: string, signal?: AbortSignal): Promise<WorkflowBinding> {
  return apiFetch<WorkflowBinding>(`/api/v2/always-on-space/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'PUT',
    signal: signal ?? null,
  });
}

/** DELETE /api/v2/always-on-space/workflows/:id (AON-07): moves the workflow
 *  out of the space, back to the Private area. Answers 204. */
export function unbindWorkflow(workflowId: string, signal?: AbortSignal): Promise<void> {
  return apiFetch<void>(`/api/v2/always-on-space/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'DELETE',
    signal: signal ?? null,
  });
}

/** POST /api/v2/always-on-space/off (AON-08): switches the space off (the
 *  server key is destroyed). Returns the status after the switch. */
export function switchOffAlwaysOnSpace(opts?: { signal?: AbortSignal }): Promise<AlwaysOnSpaceStatus> {
  return apiFetch<AlwaysOnSpaceStatus>('/api/v2/always-on-space/off', {
    method: 'POST',
    signal: opts?.signal ?? null,
  });
}

/** GET /api/v2/always-on-space/access-log (AON-08): one page of the access
 *  log, newest first. `before` is the `next` cursor of the previous page.
 *  423 `not_mounted` when the space is not mounted in this session. */
export function getAlwaysOnAccessLog(opts?: {
  limit?: number;
  before?: string;
  signal?: AbortSignal;
}): Promise<AlwaysOnAccessLogPage> {
  const q = new URLSearchParams();
  if (opts?.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts?.before) q.set('before', opts.before);
  const qs = q.toString();
  return apiFetch<AlwaysOnAccessLogPage>(`/api/v2/always-on-space/access-log${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    signal: opts?.signal ?? null,
  });
}

/** The caller's always-on space (AON-03), or null when there is none. The
 *  endpoint answers 404 only when the user has no space; that is "nothing to
 *  mount", not an error. With the feature flag off and a space present it
 *  answers 200 with suspended.reason = "kill_switch": the space is still
 *  returned, because switch-off and GDPR export keep working with the flag off
 *  and both read through the owner's session DEK. Any other failure throws.
 *  `signal` (AON-08) lets the status store abort a read it timed out. */
export async function getAlwaysOnSpace(opts?: { signal?: AbortSignal }): Promise<AlwaysOnSpaceStatus | null> {
  try {
    const st = await apiFetch<AlwaysOnSpaceStatus>('/api/v2/always-on-space', {
      method: 'GET',
      signal: opts?.signal ?? null,
    });
    return st && st.workspace_id ? st : null;
  } catch (e) {
    if (e instanceof AuthError && e.status === 404) return null;
    throw e;
  }
}

/** GET /api/v2/always-on-space/org-policy's body (AON-09). */
export interface AlwaysOnOrgPolicyVerdict {
  allowed: boolean;
  blocking_orgs: AlwaysOnOrgRef[];
}

/** GET /api/v2/always-on-space/org-policy (AON-09): whether the caller's
 *  shared orgs allow an always-on space. Throws on any non-2xx, including the
 *  503 `policy_unavailable` — "not known" is never rendered as "forbidden". */
export function getAlwaysOnOrgPolicy(opts?: { signal?: AbortSignal }): Promise<AlwaysOnOrgPolicyVerdict> {
  return apiFetch<AlwaysOnOrgPolicyVerdict>('/api/v2/always-on-space/org-policy', {
    method: 'GET',
    signal: opts?.signal ?? null,
  });
}

/** Unmounts a workspace (drops the DEK from the session keyring). */
export function sendUnmountWorkspace(workspaceId: string): Promise<UnmountWorkspaceResponse> {
  const req = createProto(UnmountWorkspaceRequestSchema, { workspaceId });
  return sendRequest({ case: 'unmountWorkspaceRequest', value: req }, 'unmountWorkspaceResponse');
}

/** Triggers a revocation re-key (requires the workspace mounted). After success
 *  the client must re-fetch key-material, unwrap the NEW DEK, and re-mount. */
export function sendRekeyWorkspace(workspaceId: string): Promise<RekeyWorkspaceResponse> {
  const req = createProto(RekeyWorkspaceRequestSchema, { workspaceId });
  return sendRequest({ case: 'rekeyWorkspaceRequest', value: req }, 'rekeyWorkspaceResponse');
}

// --- Snapshots (P12) ---

export type { Snapshot, SnapshotTag, SnapshotContentResponse };

/** Freezes a workspace item into an immutable snapshot with external-event tags. */
export function createSnapshot(args: {
  workspaceId: string;
  itemKind: 'note' | 'document';
  itemId: string;
  tags: SnapshotTag[];
  notes: string;
}): Promise<SnapshotResponse> {
  const req = createProto(CreateSnapshotRequestSchema, {
    workspaceId: args.workspaceId,
    itemKind: args.itemKind,
    itemId: args.itemId,
    tags: args.tags,
    notes: args.notes,
  });
  return sendRequest({ case: 'createSnapshotRequest', value: req }, 'snapshotResponse');
}

/** Lists ALL snapshots in a workspace (metadata only). The caller filters by
 *  item client-side — the wire request is workspace-scoped. */
export function listSnapshots(workspaceId: string): Promise<ListSnapshotsResponse> {
  const req = createProto(ListSnapshotsRequestSchema, { workspaceId });
  return sendRequest({ case: 'listSnapshotsRequest', value: req }, 'listSnapshotsResponse');
}

/** Fetches one snapshot's metadata + integrity-verified frozen content. */
export function getSnapshot(workspaceId: string, snapshotId: string): Promise<SnapshotContentResponse> {
  const req = createProto(GetSnapshotRequestSchema, { workspaceId, snapshotId });
  return sendRequest({ case: 'getSnapshotRequest', value: req }, 'snapshotContentResponse');
}

/** Appends an external-event tag to an existing snapshot. */
export function addSnapshotTag(workspaceId: string, snapshotId: string, tag: SnapshotTag): Promise<SnapshotResponse> {
  const req = createProto(AddSnapshotTagRequestSchema, { workspaceId, snapshotId, tag });
  return sendRequest({ case: 'addSnapshotTagRequest', value: req }, 'snapshotResponse');
}

/** Toggles a snapshot's legal-hold flag (gates export-on-erase). */
export function setSnapshotLegalHold(workspaceId: string, snapshotId: string, legalHold: boolean): Promise<SnapshotResponse> {
  const req = createProto(SetSnapshotLegalHoldRequestSchema, { workspaceId, snapshotId, legalHold });
  return sendRequest({ case: 'setSnapshotLegalHoldRequest', value: req }, 'snapshotResponse');
}

// --- Workspace export (ws-erasure-export — GDPR legal-hold release path) ---

/**
 * Exports a MOUNTED workspace as a ZIP over the WS envelope (654 → 655). Any grant
 * level suffices; the server requires the workspace mounted in THIS session (the
 * island is E2E-encrypted — only a session holding the WS-DEK can read it) and
 * rate-limits to one export per workspace per hour. The response carries the whole
 * archive as bytes plus a suggested filename; pair with
 * {@link downloadWorkspaceExport} to hand it to the browser. A large island can
 * take a while to assemble, so the timeout is raised above the 30s default.
 */
export function sendExportWorkspace(workspaceId: string): Promise<ExportWorkspaceResponse> {
  const req = createProto(ExportWorkspaceRequestSchema, { workspaceId });
  return sendRequest({ case: 'exportWorkspaceRequest', value: req }, 'exportWorkspaceResponse', {
    timeoutMs: 120_000,
  });
}

/**
 * Hands an export response to the browser as a file download (Blob + anchor —
 * the bytes came over the WS, so there is no URL to navigate to). Object URL is
 * revoked after the click so the archive doesn't pin memory.
 */
export function downloadWorkspaceExport(resp: ExportWorkspaceResponse): void {
  // Copy into a fresh Uint8Array backed by a plain ArrayBuffer — the protobuf
  // payload may be a view into a larger (or SharedArrayBuffer-backed) buffer,
  // which BlobPart typing rejects.
  const bytes = new Uint8Array(resp.archive);
  const blob = new Blob([bytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = resp.filename || 'lumavista-workspace-export.zip';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
